import type {
  Award,
  ContactChannel,
  Distinction,
  Domain,
  ExperienceEntry,
  NavItem,
  Project,
  Reference,
  ResearchEntry,
  Service,
  SkillGroup,
  SocialLink,
  Testimonial,
  Tool,
} from '@/types/content'

/** Identity and global copy. */
export interface SiteIdentity {
  name: string
  owner: string
  role: string
  tagline: string
  bio: string
  mark: string
  portrait: string
  heroImage: string
  /** Direct link to the CV. Empty until one is uploaded. */
  resume: string
  email: string
  location: string
  /** Where `location` is, so the contact map is derived rather than pasted. */
  coordinates: { lat: number; lon: number }
  copyrightYear: number
}

export interface ResearchInterests {
  heading: string
  summary: string
  areas: { name: string; detail: string }[]
}

/**
 * Everything the admin can edit, in one document.
 *
 * This mirrors the `SiteContent` singleton in the API. Keeping it as one
 * shape — rather than a table per section — means the content model is owned
 * here, in TypeScript, and the backend only has to store JSON.
 */
export interface SiteContent {
  site: SiteIdentity
  navItems: NavItem[]
  socialLinks: SocialLink[]
  contactChannels: ContactChannel[]
  languages: string[]
  tools: Tool[]
  expertise: string[]
  expertiseStatement: string
  domains: Domain[]
  services: Service[]
  projects: Project[]
  experience: ExperienceEntry[]
  education: ExperienceEntry[]
  community: ExperienceEntry[]
  awards: Award[]
  skillGroups: SkillGroup[]
  research: ResearchEntry[]
  researchInterests: ResearchInterests
  distinctions: Distinction[]
  references: Reference[]
  testimonials: Testimonial[]
}

/** The sections the admin lists, in the order it shows them. */
export const CONTENT_SECTIONS = [
  { key: 'site', label: 'Identity & contact' },
  { key: 'projects', label: 'Projects' },
  { key: 'domains', label: 'Domains & categories' },
  { key: 'services', label: 'Services' },
  { key: 'experience', label: 'Experience' },
  { key: 'education', label: 'Education' },
  { key: 'community', label: 'Community & service' },
  { key: 'skillGroups', label: 'Technical skills' },
  { key: 'research', label: 'Research' },
  { key: 'researchInterests', label: 'Research interests' },
  { key: 'distinctions', label: 'Distinctions' },
  { key: 'awards', label: 'Certifications' },
  { key: 'references', label: 'References' },
  { key: 'testimonials', label: 'Testimonials' },
  { key: 'tools', label: 'Tool grid' },
  { key: 'expertise', label: 'Expertise marquee' },
  { key: 'expertiseStatement', label: 'Expertise statement' },
  { key: 'navItems', label: 'Navigation' },
  { key: 'socialLinks', label: 'Social links' },
  { key: 'contactChannels', label: 'Contact channels' },
] as const satisfies readonly { key: keyof SiteContent; label: string }[]

export type ContentSectionKey = (typeof CONTENT_SECTIONS)[number]['key']
